import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import DynamicLayout from '../components/DynamicLayout';
import { 
  Crown, 
  Star, 
  Zap, 
  BookOpen, 
  Users, 
  Shield, 
  MessageCircle, 
  Calendar,
  Check,
  Sparkles,
  Gem,
  Award,
  Clock,
  Heart,
  Globe,
  Lock,
  Unlock
} from 'lucide-react';
import '../assets/premium-plans.sass';

const PremiumPage = () => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');

  const plans = [
    {
      id: 'free',
      name: 'Free',
      icon: Star,
      color: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
      price: 0,
      originalPrice: 0,
      period: 'forever',
      description: 'Perfect for beginners starting their mystical journey',
      features: [
        'Access to 3 core courses',
        'Basic tools and calculators',
        'Community forum access',
        'Email support',
        'Monthly horoscope',
        'Basic birth chart analysis',
        'Tarot card readings',
        'Rune interpretations',
        'Basic numerology calculator'
      ],
      popular: false,
      savings: 'Always free'
    },
    {
      id: 'practitioner',
      name: 'Practitioner',
      icon: Crown,
      color: 'linear-gradient(135deg, #C0C0C0 0%, #A9A9A9 100%)',
      price: billingCycle === 'monthly' ? 19.99 : 199.99,
      originalPrice: billingCycle === 'monthly' ? 39.99 : 399.99,
      period: billingCycle === 'monthly' ? 'month' : 'year',
      description: 'For serious practitioners seeking advanced tools',
      features: [
        'All Free features',
        'Access to all courses (8 total)',
        'Advanced tools and calculators',
        'Priority email support',
        'Weekly live Q&A sessions',
        'Personalized study plans',
        'Advanced chart interpretations',
        'Exclusive study materials',
        'Professional tarot spreads',
        'Advanced rune casting',
        'Complex numerology analysis',
        'Custom spell crafting tools'
      ],
      popular: true,
      savings: billingCycle === 'monthly' ? '50% off' : '50% off + 2 months free'
    },
    {
      id: 'student',
      name: 'Student',
      icon: BookOpen,
      color: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
      price: billingCycle === 'monthly' ? 39.99 : 399.99,
      originalPrice: billingCycle === 'monthly' ? 79.99 : 799.99,
      period: billingCycle === 'monthly' ? 'month' : 'year',
      description: 'Complete access to the mystical university',
      features: [
        'All Practitioner features',
        'Full university access',
        '1-on-1 mentorship sessions',
        'Priority phone support',
        'Custom study materials',
        'Exclusive workshops',
        'Advanced certifications',
        'Personal mystical consultation',
        'Early access to new features',
        'Private community access',
        'University library access',
        'Degree program enrollment',
        'Research project support',
        'Academic advisor access'
      ],
      popular: false,
      savings: billingCycle === 'monthly' ? '50% off' : '50% off + 2 months free'
    }
  ];

  const handlePlanSelect = (planId) => {
    setSelectedPlan(planId);
  };

  return (
    <DynamicLayout>
      <div className="premium-plans">
        <div className="premium-container">
        {/* Header Section */}
        <div className="premium-header">
          <div className="header-content">
            <div className="header-icon">
              <Crown size={48} />
            </div>
            <div className="header-text">
              <h1>Choose Your Mystical Path</h1>
              <p>Start free or unlock advanced features with our practitioner and student plans</p>
            </div>
          </div>
          
          {/* Billing Toggle */}
          <div className="billing-toggle">
            <span className={billingCycle === 'monthly' ? 'active' : ''}>Monthly</span>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={billingCycle === 'yearly'}
                onChange={(e) => setBillingCycle(e.target.checked ? 'yearly' : 'monthly')}
              />
              <span className="toggle-slider"></span>
            </label>
            <span className={billingCycle === 'yearly' ? 'active' : ''}>
              Yearly <span className="save-badge">Save 50%</span>
            </span>
          </div>
        </div>

        {/* Plans Grid */}
        <div className="plans-grid">
          {plans.map((plan) => {
            const PlanIcon = plan.icon;
            return (
              <div 
                key={plan.id} 
                className={`plan-card ${plan.popular ? 'popular' : ''} ${selectedPlan === plan.id ? 'selected' : ''}`}
                onClick={() => handlePlanSelect(plan.id)}
              >

                
                <div className="plan-header">
                  <div className="plan-icon" style={{ background: plan.color }}>
                    <PlanIcon size={32} />
                  </div>
                  <div className="plan-info">
                    <h2>{plan.name}</h2>
                    <p>{plan.description}</p>
                  </div>
                </div>

                <div className="plan-pricing">
                  <div className="price-container">
                    {plan.price === 0 ? (
                      <>
                        <span className="price">Free</span>
                        <span className="period">/{plan.period}</span>
                      </>
                    ) : (
                      <>
                        <span className="currency">$</span>
                        <span className="price">{plan.price}</span>
                        <span className="period">/{plan.period}</span>
                      </>
                    )}
                  </div>
                  {plan.originalPrice > 0 && (
                    <div className="original-price">${plan.originalPrice}</div>
                  )}
                  <div className="savings">{plan.savings}</div>
                </div>

                <div className="plan-features">
                  {plan.features.map((feature, index) => (
                    <div key={index} className="feature-item">
                      <Check size={18} />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>

                <button className="plan-button">
                  {selectedPlan === plan.id ? 'Selected' : plan.price === 0 ? 'Get Started' : 'Choose Plan'}
                </button>
              </div>
            );
          })}
        </div>

        {/* Additional Info */}
        <div className="premium-info">
          <div className="info-section">
            <h3>Why Choose Premium?</h3>
            <div className="benefits-grid">
              <div className="benefit-item">
                <BookOpen size={24} />
                <h4>Comprehensive Learning</h4>
                <p>Access to all courses and exclusive study materials</p>
              </div>
              <div className="benefit-item">
                <Users size={24} />
                <h4>Community Access</h4>
                <p>Connect with fellow mystics and share knowledge</p>
              </div>
              <div className="benefit-item">
                <Shield size={24} />
                <h4>Expert Support</h4>
                <p>Get help from experienced practitioners</p>
              </div>
              <div className="benefit-item">
                <Zap size={24} />
                <h4>Advanced Tools</h4>
                <p>Professional-grade calculators and analysis tools</p>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="faq-section">
          <h3>Frequently Asked Questions</h3>
          <div className="faq-grid">
            <div className="faq-item">
              <h4>Can I cancel anytime?</h4>
              <p>Yes, you can cancel your subscription at any time with no cancellation fees.</p>
            </div>
            <div className="faq-item">
              <h4>Do you offer refunds?</h4>
              <p>We offer a 30-day money-back guarantee for all premium plans.</p>
            </div>
            <div className="faq-item">
              <h4>Can I upgrade my plan?</h4>
              <p>Yes, you can upgrade or downgrade your plan at any time.</p>
            </div>
            <div className="faq-item">
              <h4>Is there a free trial?</h4>
              <p>Yes, all plans come with a 7-day free trial to test the features.</p>
            </div>
          </div>
        </div>
      </div>
      </div>
    </DynamicLayout>
  );
};

export default PremiumPage;

